import { Check, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { StructuredData } from "@/components/StructuredData";
import { SiteLayout } from "@/components/site/SiteLayout";
import { SHOPIFY_APP_STORE_URL } from "@/lib/appLinks";
import { pricingPageSchema } from "@/lib/seoSchema";

const tiers = [
  {
    name: "Free",
    price: "$0",
    desc: "Perfect for test stores and brands getting started with split payments.",
    cta: "Install Free",
    features: ["After trial: 5 free splits every billing cycle", "Unlimited test orders", "Full feature access", "Community support"],
    accent: false,
  },
  {
    name: "Starter",
    price: "$11.99",
    desc: "Lower monthly pricing for stores that want more free splits and lower overage fees.",
    cta: "Choose Starter",
    features: ["1 month free trial, then $11.99/month", "25 free splits every billing cycle", "1.5% fee after the free split allowance", "Unlimited split links", "Priority support"],
    accent: true,
    badge: "Most popular",
  },
  {
    name: "Growth",
    price: "$29.99",
    desc: "Best for stores running high split volume with the lowest overage fee.",
    cta: "Choose Growth",
    features: ["1 month free trial, then $29.99/month", "100 free splits every billing cycle", "1% fee after the free split allowance", "Unlimited split links", "Priority support"],
    accent: false,
  },
];

function Pricing() {
  return (
    <SiteLayout>
      <SEO
        title="Pricing"
        description="Splitkart pricing for Shopify merchants. Free for development stores, a flat monthly plan for live brands, and custom support for Plus teams."
        url="/pricing"
      />
      <StructuredData data={pricingPageSchema()} />

      <section className="section bg-gradient-hero">
        <div className="container-tight text-center">
          <span className="eyebrow">Pricing</span>
          <h1 className="mt-5 text-balance text-4xl font-bold md:text-6xl">Simple, flat pricing.</h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">
            No hidden fees. We win when you sell more high-ticket items.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-tight">
          <div className="grid gap-6 md:grid-cols-3">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className={`relative flex flex-col rounded-2xl border p-8 transition-all ${
                  tier.accent
                    ? "border-primary bg-card shadow-elevated md:scale-[1.02]"
                    : "border-border bg-card shadow-soft hover:shadow-elevated"
                }`}
              >
                {tier.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">
                    {tier.badge}
                  </span>
                )}
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-primary">
                  <Sparkles className="h-5 w-5" />
                </div>
                <h2 className="mt-5 text-lg font-bold">{tier.name}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{tier.desc}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="text-5xl font-bold tracking-tight">{tier.price}</span>
                </div>
                <ul className="mt-8 space-y-3">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={SHOPIFY_APP_STORE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className={`mt-8 inline-flex items-center justify-center rounded-lg px-4 py-3 text-sm font-semibold transition-all ${
                    tier.accent
                      ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-soft"
                      : "border border-border bg-background hover:border-primary hover:text-primary"
                  }`}
                >
                  {tier.cta}
                </a>
              </div>
            ))}
          </div>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            Pricing and billing for Shopify merchants follow the current Shopify App Store listing.
          </p>

          <p className="mt-3 text-center text-sm text-muted-foreground">
            Every new merchant starts with 30 days, up to 100 free splits, and 0% commission before regular plan billing starts.
          </p>

          <p className="mt-12 text-center text-sm text-muted-foreground">
            Questions about pricing?{" "}
            <Link to="/faq" className="font-semibold text-primary hover:underline">
              See the FAQ →
            </Link>
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}

export default Pricing;
